import { useEffect, useMemo } from "react";
import { FiUserMinus, FiUserPlus } from "react-icons/fi";
import Tooltip from "../shared/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { getSentFollowRequestsAction } from "../../redux/actions/userActions";
import { getSentProfileViewRequestsAction } from "../../redux/actions/profileViewRequestActions";

const ProfileInfoCard = ({ 
  userProfile, 
  publicUserId, 
  publicUsername,
  isFollowing, 
  isModerator, 
  followLoading, 
  unfollowLoading, 
  handleFollow, 
  handleUnfollow,
  accessDenied,
  accessReason,
  accessMessage,
  isAuthenticated = true,
  onRequireAuth,
  onViewProfileClick
}) => {
  const dispatch = useDispatch();
  
  // Get sent profile view requests from Redux
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);
  // Get sent follow requests from Redux
  const sentFollowRequests = useSelector((state) => state.user?.sentFollowRequests || []);

  // Find existing profile view request for this profile owner
  const existingProfileViewRequest = useMemo(() => {
    if (!publicUserId || !sentProfileViewRequests || sentProfileViewRequests.length === 0) {
      return null;
    }
    
    const found = sentProfileViewRequests.find((request) => {
      // Handle both cases: profileOwner as object with _id or as string ID
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      const matches = profileOwnerId && String(profileOwnerId) === String(publicUserId);
      return matches;
    });
    
    return found;
  }, [sentProfileViewRequests, publicUserId]);

  // Find existing follow request for this user
  const existingFollowRequest = useMemo(() => {
    if (!publicUserId || !sentFollowRequests || sentFollowRequests.length === 0) {
      return null;
    }
    
    const found = sentFollowRequests.find((request) => {
      // Handle both cases: recipient as object with _id or as string ID
      const recipientId = request.recipient?._id || request.recipient;
      const matches = recipientId && String(recipientId) === String(publicUserId);
      return matches && request.status === "pending";
    });
    
    return found;
  }, [sentFollowRequests, publicUserId]);

  // Refresh sent requests when component mounts or when publicUserId changes
  useEffect(() => {
    if (isAuthenticated && publicUserId) {
      dispatch(getSentProfileViewRequestsAction());
      dispatch(getSentFollowRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, publicUserId]);

  // Debug: Log access state (remove in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProfileInfoCard access state:', { 
        accessDenied, 
        accessReason, 
        accessMessage, 
        existingProfileViewRequest,
        existingFollowRequest,
        publicUserId,
        sentProfileViewRequestsCount: sentProfileViewRequests?.length 
      });
    }
  }, [accessDenied, accessReason, accessMessage, existingProfileViewRequest, existingFollowRequest, publicUserId, sentProfileViewRequests]);
  const {
    name,
    avatar,
    email,
    gender,
    dob,
    mobileNumber,
    city,
    country,
    role,
    followingSince,
  } = userProfile;

  const Button = ({ loading, onClick, tooltipText, icon, color }) => (
    <button
      onClick={onClick}
      type="button"
      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${color} bg-white hover:bg-gray-50`}
      disabled={loading}
    >
      {loading ? (
        <span className="text-xs font-flex-regular">Wait</span>
      ) : (
        <>
          <Tooltip text={tooltipText}>{icon}</Tooltip>
          <span className="text-xs font-flex-regular">
            {tooltipText.includes('Follow') ? 'Follow' : 'Unfollow'}
          </span>
        </>
      )}
    </button>
  );

  const FollowButton = ({ loading, onClick, name }) => (
    <Button
      loading={loading}
      onClick={onClick}
      tooltipText={`Follow ${name}`}
      icon={<FiUserPlus />}
      color="text-primary border-primary"
    />
  );

  const UnfollowButton = ({ loading, onClick, name }) => (
    <Button
      loading={loading}
      onClick={onClick}
      tooltipText={`Unfollow ${name}`}
      icon={<FiUserMinus />}
      color="text-action border-action"
    />
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex flex-col items-center">
        <div className="flex-shrink-0 mb-4">
          <img
            className="h-24 w-24 rounded-full object-cover border-4 border-gray-300 shadow-lg"
            src={avatar}
            alt="Profile"
            loading="lazy"
          />
        </div>

        <div className="w-full">
          <h1 className="text-xl font-semibold text-gray-900 font-flex-regular mb-3 text-center">
            {name}
          </h1>
          
          {/* Follow/Unfollow section */}
          <div className="flex flex-col items-center mb-4">
            {!isAuthenticated && !isModerator && (
              <button
                type="button"
                onClick={() => {
                  if (onRequireAuth) onRequireAuth("follow");
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-primary border-primary bg-white hover:bg-gray-50"
              >
                <FiUserPlus className="w-4 h-4" />
                <span className="font-flex-regular">Sign in to Follow</span>
              </button>
            )}

            {isAuthenticated && (
              <>
                {accessDenied ? (
                  // Enhanced buttons when access is denied
                  <>
                    {isFollowing && !isModerator && (
                      <button
                        onClick={() => handleUnfollow(publicUserId)}
                        disabled={unfollowLoading}
                        type="button"
                        className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-action border-action bg-action/5 hover:bg-action/10 hover:border-action/80 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {unfollowLoading ? (
                          <span className="text-xs font-flex-regular">Wait</span>
                        ) : (
                          <>
                            <FiUserMinus className="w-4 h-4" />
                            <span className="font-flex-regular">Unfollow</span>
                          </>
                        )}
                      </button>
                    )}
                    {!isModerator && !isFollowing && (
                      existingFollowRequest ? (
                        <button
                          disabled
                          type="button"
                          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-gray-500 border-gray-300 bg-gray-50 cursor-not-allowed"
                        >
                          <span className="font-flex-regular">Request Pending</span>
                        </button>
                      ) : (() => {
                        const followPrivacy = userProfile?.privacySettings?.followPrivacy || "anyone";
                        const profileOwnerFollowsCurrentUser = userProfile?.profileOwnerFollowsCurrentUser || false;
                        const canFollow = followPrivacy === "anyone" || profileOwnerFollowsCurrentUser;
                        
                        if (!canFollow) {
                          return (
                            <button
                              disabled
                              type="button"
                              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-gray-500 border-gray-300 bg-gray-50 cursor-not-allowed"
                              title="You can follow this user only after they follow you"
                            >
                              <FiUserPlus className="w-4 h-4" />
                              <span className="font-flex-regular">Follow</span>
                            </button>
                          );
                        }
                        
                        return (
                          <button
                            onClick={() => handleFollow(publicUserId)}
                            disabled={followLoading}
                            type="button"
                            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 text-primary border-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/80 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {followLoading ? (
                              <span className="text-xs font-flex-regular">Wait</span>
                            ) : (
                              <>
                                <FiUserPlus className="w-4 h-4" />
                                <span className="font-flex-regular">Follow</span>
                              </>
                            )}
                          </button>
                        );
                      })()
                    )}
                  </>
                ) : (
                  // Standard buttons when access is granted
                  <>
                    {isFollowing && !isModerator && (
                      <UnfollowButton
                        loading={unfollowLoading}
                        onClick={() => handleUnfollow(publicUserId)}
                        name={name}
                      />
                    )}
                    {!isModerator && !isFollowing && (
                      existingFollowRequest ? (
                        <button
                          disabled
                          type="button"
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors text-gray-500 border-gray-300 bg-gray-50 cursor-not-allowed"
                        >
                          <span className="text-xs font-flex-regular">Request Pending</span>
                        </button>
                      ) : (() => {
                        const followPrivacy = userProfile?.privacySettings?.followPrivacy || "anyone";
                        const profileOwnerFollowsCurrentUser = userProfile?.profileOwnerFollowsCurrentUser || false;
                        const canFollow = followPrivacy === "anyone" || profileOwnerFollowsCurrentUser;
                        
                        if (!canFollow) {
                          return (
                            <button
                              disabled
                              type="button"
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors text-gray-500 border-gray-300 bg-gray-50 cursor-not-allowed"
                              title="You can follow this user only after they follow you"
                            >
                              <FiUserPlus className="w-4 h-4" />
                              <span className="text-xs font-flex-regular">Follow</span>
                            </button>
                          );
                        }
                        
                        return (
                          <FollowButton
                            loading={followLoading}
                            onClick={() => handleFollow(publicUserId)}
                            name={name}
                          />
                        );
                      })()
                    )}
                  </>
                )}
              </>
            )}
            
            {/* Following since - shows based on following status, not access status */}
            {isFollowing && followingSince && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-2">
                <svg className="w-3.5 h-3.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium text-primary-800">Following since {followingSince}</span>
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm text-gray-600 text-left">
            {/* Only show sensitive info if access is granted */}
            {!accessDenied && email && (
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-flex-regular text-sm text-gray-700 leading-relaxed">{email}</span>
              </div>
            )}
            {!accessDenied && gender && (
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-flex-regular text-sm text-gray-700 leading-relaxed">{gender}</span>
              </div>
            )}
            {!accessDenied && dob && (
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-flex-regular text-sm text-gray-700 leading-relaxed">
                  {new Date(Number(dob)).toLocaleDateString('en-GB')}
                </span>
              </div>
            )}
            {!accessDenied && mobileNumber && (
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-flex-regular text-sm text-gray-700 leading-relaxed">{mobileNumber}</span>
              </div>
            )}
            {!accessDenied && (city || country) && (
              <div className="flex gap-3">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-flex-regular text-sm text-gray-700 leading-relaxed m-0">
                    {[city, country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}
            {/* Show message if access is denied */}
            {accessDenied && (
              <div className="flex items-start gap-3 mt-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="font-flex-regular text-sm text-gray-500 italic">
                  {accessMessage || "This profile is private"}
                </span>
              </div>
            )}
            {role === "moderator" && (
              <div className="flex items-start gap-3">
                <div className="w-4 h-4 flex-shrink-0 mt-0.5"></div>
                <span className="rounded-md bg-sky-200 px-2 py-1 text-sm font-semibold text-sky-700 font-flex-regular">
                  Moderator
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileInfoCard;
